import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Search, Globe, MapPin, Zap, Award, Shield, AlertTriangle,
  Loader2, ArrowRight, ExternalLink, Clock, Share2, Copy, Check
} from 'lucide-react';

const CATEGORY_META = {
  gbp_readiness: { label: 'GBP Readiness', icon: MapPin, color: '#0268C4' },
  website_relevance: { label: 'Website Relevance', icon: Globe, color: '#22C55E' },
  technical_seo: { label: 'Technical SEO', icon: Zap, color: '#DDA73D' },
  prominence: { label: 'Prominence', icon: Award, color: '#64748B' },
  risk_compliance: { label: 'Risk & Compliance', icon: Shield, color: '#EF4444' }
};
const IMPACT_COLORS = { High: '#EF4444', Med: '#DDA73D', Low: '#22C55E' };
const EFFORT_COLORS = { S: '#22C55E', M: '#DDA73D', L: '#EF4444' };

export default function SharedReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/shared-reports/${token}`)
      .then(r => setReport(r.data))
      .catch(err => {
        const status = err.response?.status;
        if (status === 410) setError('This report has expired. Ask the sender to generate a new one.');
        else if (status === 404) setError('Report not found. The link may be incorrect or expired.');
        else setError('Something went wrong loading this report.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const scoreColor = (s) => s > 80 ? '#22C55E' : s > 50 ? '#DDA73D' : '#EF4444';
  const scoreLabel = (s) => s > 80 ? 'Healthy' : s > 50 ? 'Needs Work' : 'Critical';

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#0268C4] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7F9FC]">
        <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E2E8F0]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-2">
              <img src="/icon-nav.png" alt="Local Luc" className="h-12 w-12 rounded-xl object-cover" />
            </Link>
          </div>
        </nav>
        <div className="max-w-lg mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 bg-[#EF4444]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-[#EF4444]" />
          </div>
          <h1 className="font-heading text-2xl font-medium text-[#0F2951] mb-3">{error}</h1>
          <Link to="/">
            <Button data-testid="shared-run-own-audit" className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-6 h-11 mt-4">
              Run Your Own Free Audit <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const r = report;
  const createdDate = r.created_at ? new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-[#F7F9FC] pb-16">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="shared-logo">
            <img src="/icon-nav.png" alt="Local Luc" className="h-12 w-12 rounded-xl object-cover" />
          </Link>
          <div className="flex items-center gap-3">
            <Button
              data-testid="shared-copy-link"
              variant="outline"
              size="sm"
              onClick={copyLink}
              className="border-[#E2E8F0] text-[#64748B] hover:bg-[#EFF3F8] rounded-xl h-9 px-3"
            >
              {copied ? <Check className="w-4 h-4 mr-1.5 text-[#22C55E]" /> : <Copy className="w-4 h-4 mr-1.5" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            <Link to="/">
              <Button data-testid="shared-run-audit-cta" className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-4 h-9 text-sm">
                Run Your Audit
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Shared badge */}
        <div className="flex items-center gap-2 text-sm text-[#64748B]">
          <Share2 className="w-4 h-4 text-[#0268C4]" />
          <span>Shared report &middot; {createdDate}</span>
          {r.website_url && (
            <a href={r.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#0268C4] hover:underline ml-auto">
              {r.website_url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')} <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        {/* Health Score */}
        <div data-testid="shared-health-score" className="bg-white rounded-2xl border border-[#E2E8F0] p-6 sm:p-8 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="flex flex-col items-center">
              <div className="relative w-28 h-28">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#EFF3F8" strokeWidth="8" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke={scoreColor(r.overall_score)} strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={`${r.overall_score * 2.64} 264`} className="transition-all duration-1000" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-heading text-3xl font-semibold" style={{ color: scoreColor(r.overall_score) }}>{r.overall_score}</span>
                </div>
              </div>
              <span className="text-sm font-medium mt-1" style={{ color: scoreColor(r.overall_score) }}>{scoreLabel(r.overall_score)}</span>
              <span className="text-xs text-[#64748B]">Health Score</span>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#0F2951] mb-1">
                {r.business_name ? `${r.business_name}'s` : 'Online'} Visibility Report
              </h1>
              <p className="text-sm text-[#64748B]">
                {r.business_type}{r.city ? ` in ${r.city}` : ''}{r.state ? `, ${r.state}` : ''} &middot; {r.page_count} pages analyzed &middot; {r.issues?.length || 0} issues found
              </p>
              {r.primary_goal && <p className="text-sm text-[#0268C4] mt-1">Goal: {r.primary_goal}</p>}
              {r.intention && !r.primary_goal && <p className="text-sm text-[#0268C4] mt-1">Focus: {r.intention}</p>}
            </div>
          </div>

          {/* Category scores */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
            {Object.entries(r.category_scores || {}).map(([cat, score]) => {
              const meta = CATEGORY_META[cat];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <div key={cat} className="bg-[#F7F9FC] rounded-xl p-3 text-center">
                  <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: meta.color }} strokeWidth={1.5} />
                  <div className="font-heading text-xl font-semibold" style={{ color: scoreColor(score) }}>{score}</div>
                  <div className="text-[10px] text-[#64748B] mt-0.5">{meta.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Recommendations */}
        {r.recommendations?.length > 0 && (
          <div data-testid="shared-recommendations" className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
            <h3 className="font-heading text-lg font-medium text-[#0F2951] mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-[#0268C4]" /> Top Moves
            </h3>
            <div className="space-y-3">
              {r.recommendations.slice(0, 5).map((rec, i) => (
                <div key={i} data-testid={`shared-move-${i}`} className="flex items-start gap-3 p-3 bg-[#F7F9FC] rounded-xl">
                  <div className="w-6 h-6 bg-[#0268C4] rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0F2951]">{rec.title}</p>
                    <p className="text-xs text-[#64748B] mt-0.5">{rec.description}</p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <Badge variant="outline" className="text-[10px] rounded-lg border-[#E2E8F0] px-1.5" style={{ color: IMPACT_COLORS[rec.impact] }}>{rec.impact}</Badge>
                    <Badge variant="outline" className="text-[10px] rounded-lg border-[#E2E8F0] px-1.5" style={{ color: EFFORT_COLORS[rec.effort] }}>{rec.effort}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Issues */}
        {r.issues?.length > 0 && (
          <div data-testid="shared-issues" className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
            <h3 className="font-heading text-lg font-medium text-[#0F2951] mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#EF4444]" /> Issues Found ({r.issues.length})
            </h3>
            <div className="space-y-2">
              {r.issues.slice(0, 10).map((issue, i) => (
                <div key={i} data-testid={`shared-issue-${i}`} className="flex items-start gap-3 p-3 rounded-xl bg-[#F7F9FC]">
                  <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: IMPACT_COLORS[issue.impact] }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[#0F2951]">{issue.title}</span>
                      <Badge variant="outline" className="text-[10px] rounded-lg border-[#E2E8F0] text-[#64748B] flex-shrink-0">{CATEGORY_META[issue.category]?.label || issue.category}</Badge>
                    </div>
                    {issue.description && <p className="text-xs text-[#64748B] mt-1">{issue.description}</p>}
                    {issue.why_it_matters && <p className="text-xs text-[#64748B] mt-1 italic">Why it matters: {issue.why_it_matters}</p>}
                  </div>
                </div>
              ))}
              {r.issues.length > 10 && (
                <p className="text-sm text-[#64748B] text-center pt-2">+ {r.issues.length - 10} more issues</p>
              )}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-[#0F2951] rounded-2xl p-6 sm:p-8 text-center">
          <img src="/icon-nav.png" alt="Local Luc" className="w-14 h-14 rounded-xl object-cover mx-auto mb-3" />
          <h3 className="font-heading text-xl font-medium text-white mb-2">Want to track your progress?</h3>
          <p className="text-sm text-white/70 mb-5 max-w-md mx-auto">
            Create a free Local Luc account to save your audit, set goals, download PDF reports, and get reminders to keep improving.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/register">
              <Button data-testid="shared-register-cta" className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-8 h-11 font-medium">
                Create Free Account <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={copyLink}
              className="border-white/20 text-white hover:bg-white/10 rounded-xl px-5 h-11"
            >
              {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
              {copied ? 'Copied!' : 'Share Again'}
            </Button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="p-4 bg-[#EFF3F8] rounded-xl border border-[#E2E8F0]">
          <p className="text-xs text-[#64748B]">
            <strong>Disclaimer:</strong> Rankings depend on relevance, distance, and prominence. No tool can guarantee specific ranking positions.
            This report was generated by <Link to="/" className="text-[#0268C4] hover:underline">Local Luc</Link> and expires 30 days after creation.
          </p>
        </div>
      </div>
    </div>
  );
}
